import { useThrottledCallback } from '@payaca/hooks';
import { Idiomorph } from 'idiomorph';
import { Liquid } from 'liquidjs';
import { useEffect, useMemo, useRef } from 'react';

// Throttle window for the preview render. The first change fires immediately
// (leading edge) so typing feels responsive; further changes within the window
// coalesce into a single trailing render with the latest value, capping the
// cost of liquid.render + Idiomorph.morph during sustained input bursts.
const RENDER_THROTTLE_MS = 150;

type UseLivePreviewArgs = {
  template: string;
  context: unknown;
  locale?: string;
};

const useLivePreview = (args: UseLivePreviewArgs) => {
  const { template, context: rawContext, locale } = args;
  const context = useMemo<object>(
    () => (rawContext && typeof rawContext === 'object' ? rawContext : {}),
    [rawContext]
  );
  const hostRef = useRef<HTMLDivElement>(null);

  // `outputEscape: 'escape'` makes `{{ var }}` HTML-escape user-supplied
  // values. The preview pane then morphs that output into a Shadow DOM via
  // Idiomorph, which uses innerHTML semantics — without escaping, an
  // attacker-controlled section heading could inject markup like
  // `<img src=x onerror=...>` and execute script.
  //
  // `locale` flows from the account region so Liquid's `date` filter renders
  // month/day names in the expected language.
  const liquid = useMemo(
    () => new Liquid({ outputEscape: 'escape', locale }),
    [locale]
  );

  const parsedTemplate = useMemo(
    () => liquid.parse(template),
    [liquid, template]
  );

  const renderPreview = useThrottledCallback(() => {
    const host = hostRef.current;
    if (!host) return;
    const shadowRoot = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    void (async () => {
      // liquidjs types `render` as Promise<any>; normalise to string so the
      // value flows safely into Idiomorph.
      const html = String(await liquid.render(parsedTemplate, context));
      Idiomorph.morph(shadowRoot, html, { morphStyle: 'innerHTML' });
    })();
  }, RENDER_THROTTLE_MS);

  useEffect(() => {
    renderPreview();
  }, [parsedTemplate, context, renderPreview]);

  return hostRef;
};

export default useLivePreview;
